/**
 * Help & Support dialog — opened from the sidebar. Submits a query to
 * POST /api/support/contact (stored in SupportRequest, viewable in the admin
 * portal). Shows support@4klabs.ai as a direct fallback.
 */

import { useEffect, useState } from 'react';
import { Loader2, CheckCircle2, LifeBuoy } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import api from '../../services/api';
import { useAuth } from '../../hooks/useAuth';

export const SUPPORT_EMAIL = 'support@4klabs.ai';

const CATEGORIES = [
  { value: 'technical', label: 'Something is broken' },
  { value: 'billing', label: 'Billing & credits' },
  { value: 'account', label: 'Account & access' },
  { value: 'feature', label: 'Feature request' },
  { value: 'other', label: 'Something else' },
];

export function SupportDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [category, setCategory] = useState('technical');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prefill the email from the signed-in user when the dialog opens.
  useEffect(() => {
    if (open) {
      setEmail((prev) => prev || user?.email || '');
      setDone(false);
      setError(null);
    }
  }, [open, user?.email]);

  const canSubmit = !busy && email.trim().length > 3 && message.trim().length >= 5;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post('/support/contact', {
        email: email.trim(),
        name: (user as any)?.displayName || undefined,
        category,
        message: message.trim(),
        source: 'help_menu',
      });
      setDone(true);
      setMessage('');
    } catch (err: any) {
      const raw = err?.response?.data?.error;
      setError(
        typeof raw === 'string' ? raw : raw?.message || 'Could not send. Please email us directly.'
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-2 bg-primary">
            <LifeBuoy className="w-6 h-6 text-white" />
          </div>
          <DialogTitle>Help &amp; Support</DialogTitle>
          <DialogDescription>
            Tell us what's going on and we'll get back to you. Or email{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">
              {SUPPORT_EMAIL}
            </a>
            .
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="flex flex-col items-center text-center gap-3 py-6">
            <CheckCircle2 className="h-10 w-10 text-primary" />
            <div className="text-lg font-semibold">Got it — we'll be in touch</div>
            <p className="text-sm text-muted-foreground max-w-sm">
              Thanks for reaching out. We'll reply to <span className="font-mono">{email}</span>.
            </p>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Your email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                disabled={busy}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Topic</Label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={busy}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium">How can we help?</Label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                placeholder="Describe the issue or question. Include the project name or what you were doing if relevant."
                disabled={busy}
              />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <Button className="w-full" disabled={!canSubmit} onClick={submit}>
              {busy ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</>
              ) : (
                'Send message'
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
